// PalavraMinutoCard — vídeo pastoral curto (≤89s) vinculado ao Devocional.
// Suporta duas origens:
//   - uploaded_file: <video> HTML5 servido pelo backend
//   - external_url: YouTube (iframe) ou MP4 direto (video HTML5)
// Some completamente se não houver conteúdo publicado para o dia
// (sem placeholder, sem "Em breve").
import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2, Share2 } from "lucide-react";
import { api } from "../lib/api";

export default function PalavraMinutoCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    let cancelled = false;
    api.get("/palavra-minuto/today")
      .then((r) => { if (!cancelled) setData(r?.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Silêncio elegante: sem vídeo válido → bloco desaparece completamente.
  if (loading || !data || !data.title) return null;
  const isExternal = data.source_type === "external_url";
  const ytId = data.youtube_video_id;
  const isYouTube = isExternal && !!ytId;
  const externalMp4 = isExternal && !ytId ? data.video_url : null;
  const uploadedSrc = !isExternal && data.video_url
    ? (data.video_url.startsWith("http") ? data.video_url : `${backend}${data.video_url}`)
    : null;
  if (!isYouTube && !externalMp4 && !uploadedSrc) return null;

  const thumbSrc = data.thumbnail_url
    ? (data.thumbnail_url.startsWith("http") ? data.thumbnail_url : `${backend}${data.thumbnail_url}`)
    : (isYouTube ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const expand = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
  };
  const share = async () => {
    const url = isYouTube
      ? `https://youtube.com/watch?v=${ytId}`
      : (window.location.origin + "/");
    const shareData = {
      title: data.title,
      text: data.description ? `${data.title} · ${data.description}` : data.title,
      url,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); }
      catch { /* cancelado */ }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(`${data.title} · ${url}`);
      } catch { /* silent */ }
    }
  };

  return (
    <section
      className="max-w-5xl mx-auto section-padding mt-8"
      data-testid="home-palavra-minuto-section"
      data-source={data.source_type || "uploaded_file"}
    >
      <div
        className="rounded-2xl border border-navy/10 bg-white overflow-hidden shadow-sm"
        data-testid="palavra-minuto-card"
      >
        <div className="p-5 sm:p-6">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Palavra em 1 Minuto</p>
          <h2 className="font-display text-xl sm:text-2xl text-navy mt-1 leading-snug" data-testid="palavra-minuto-title">
            {data.title}
          </h2>
          <p className="text-sm text-navy/60 mt-1">Uma breve reflexão sobre o Devocional de Hoje.</p>
          {data.description && (
            <p className="mt-2 text-[15px] text-navy/80 leading-relaxed" data-testid="palavra-minuto-description">
              {data.description}
            </p>
          )}
        </div>

        {isYouTube ? (
          <div className="relative bg-black mx-auto" style={{ aspectRatio: "9 / 16", maxHeight: 640, maxWidth: 360 }}>
            <iframe
              src={`https://www.youtube.com/embed/${ytId}?rel=0&playsinline=1`}
              title={data.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="palavra-minuto-youtube"
            />
          </div>
        ) : (
          <div className="relative bg-navy mx-auto" style={{ aspectRatio: "9 / 16", maxHeight: 640, maxWidth: 360 }}>
            <video
              ref={videoRef}
              src={uploadedSrc || externalMp4}
              poster={thumbSrc || undefined}
              playsInline
              preload="metadata"
              className="w-full h-full object-contain focus:outline-none focus:ring-2 focus:ring-gold"
              data-testid="palavra-minuto-video"
              onEnded={() => setPlaying(false)}
              aria-label={`Vídeo: ${data.title}`}
            />
            {!playing && (
              <button
                type="button"
                onClick={togglePlay}
                aria-label={`Reproduzir vídeo: ${data.title}`}
                data-testid="palavra-minuto-play"
                className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40 focus:outline-none focus:ring-4 focus:ring-gold/60 transition-colors"
              >
                <span className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                  <Play className="w-7 h-7 text-navy" fill="currentColor" />
                </span>
              </button>
            )}
          </div>
        )}

        <div className="p-4 flex items-center gap-3 border-t border-navy/10">
          {!isYouTube && (
            <>
              <button onClick={togglePlay} data-testid="palavra-minuto-toggle" aria-label={playing ? "Pausar" : "Reproduzir"} className="w-9 h-9 rounded-full bg-navy text-white flex items-center justify-center hover:bg-gold hover:text-navy focus:outline-none focus:ring-2 focus:ring-gold transition-colors">
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <button onClick={toggleMute} data-testid="palavra-minuto-mute" aria-label={muted ? "Ativar som" : "Silenciar"} aria-pressed={!muted} className="w-9 h-9 rounded-full border border-navy/20 flex items-center justify-center text-navy hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold transition-colors">
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button onClick={expand} data-testid="palavra-minuto-expand" aria-label="Expandir vídeo em tela cheia" className="w-9 h-9 rounded-full border border-navy/20 flex items-center justify-center text-navy hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold transition-colors">
                <Maximize2 className="w-4 h-4" />
              </button>
            </>
          )}
          <button onClick={share} data-testid="palavra-minuto-share" aria-label="Compartilhar Palavra em 1 Minuto" className="ml-auto w-9 h-9 rounded-full border border-navy/20 flex items-center justify-center text-navy hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold transition-colors">
            <Share2 className="w-4 h-4" />
          </button>
        </div>
        {data.caption && (
          <div className="px-5 pb-5 pt-1" data-testid="palavra-minuto-caption">
            <p className="text-xs uppercase tracking-[0.25em] text-navy/50">Legenda</p>
            <p className="mt-1 text-sm text-navy/80 leading-relaxed italic">
              {data.caption}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
